import type { Role } from "@agentry/zerovia-contracts";
import { useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAuth, useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	DefinitionList,
	Field,
	LinkButton,
	Metric,
	PageHeader,
	Select,
	StatusBadge,
	Table,
	TextInput,
	Toggle,
} from "../../ui/primitives";
import { EmptyState, ErrorState, LoadingState, recoveryFor } from "../../ui/states";
import { formatDateTime } from "../end-user/ProgressPages";

const ROLE_OPTIONS: Array<{ value: Role; label: string }> = [
	{ value: "tenant_member", label: "利用者" },
	{ value: "tenant_admin", label: "組織管理者" },
];

function ActionError({ error }: { error: unknown }) {
	if (!error) return null;
	const recovery = recoveryFor(error);
	return (
		<div
			role="alert"
			className="mt-3 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
		>
			<p className="text-[13px] font-bold text-danger-600">{recovery.title}</p>
			<p className="text-[12.5px] text-ink-700">{recovery.hint}</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function TenantDashboardPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(async () => {
		const [dashboard, members, budgets, audit] = await Promise.all([
			client.getDashboard(scope),
			client.listMembers(scope),
			client.listBudgets(scope),
			client.listAuditEvents(scope, { limit: 8 }),
		]);
		return { dashboard, members, budgets, audit };
	}, [client, scope.tenantId, scope.projectId]);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="ダッシュボード"
				description="利用状況、予算、最近の設定変更をまとめて確認します。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="space-y-5">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<Metric
							label="メンバー"
							value={data.data.members.filter((m) => m.status === "ACTIVE").length}
							unit="人"
						/>
						<Metric label="依頼" value={data.data.dashboard.summary.sessions} unit="件" />
						<Metric
							label="進行中"
							value={data.data.dashboard.summary.runningExecutions}
							unit="件"
						/>
						<Metric
							label="今月の費用"
							value={`¥${Math.round(data.data.dashboard.summary.cost).toLocaleString()}`}
						/>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<Card
							title="予算の消化"
							action={
								<Link
									to="/admin/budget"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									設定する
								</Link>
							}
						>
							<ul className="space-y-3">
								{data.data.budgets.map((budget) => {
									const used = budget.consumedAmount + budget.reservedAmount;
									const percent =
										budget.limitAmount === 0 ? 0 : Math.round((used / budget.limitAmount) * 100);
									return (
										<li key={budget.id}>
											<div className="flex items-center justify-between text-[13px]">
												<span className="text-ink-800">{budget.scopeLabel}</span>
												<span
													className={
														percent >= 90 ? "font-semibold text-danger-600" : "text-ink-600"
													}
												>
													{percent}%
												</span>
											</div>
											<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
												<div
													className={
														percent >= 90 ? "h-full bg-danger-500" : "h-full bg-accent-500"
													}
													style={{ width: `${Math.min(percent, 100)}%` }}
												/>
											</div>
											<p className="mt-0.5 text-[12px] text-ink-500">
												¥{used.toLocaleString()} / ¥{budget.limitAmount.toLocaleString()}（超過時:{" "}
												{budget.onExceeded === "BLOCK"
													? "実行停止"
													: budget.onExceeded === "REQUIRE_APPROVAL"
														? "承認必須"
														: "警告のみ"}
												）
											</p>
										</li>
									);
								})}
							</ul>
						</Card>

						<Card
							title="最近の設定変更"
							action={
								<Link
									to="/admin/audit"
									className="text-[13px] font-semibold text-brand-600 hover:underline"
								>
									監査ログ
								</Link>
							}
						>
							<ul className="divide-y divide-ink-100">
								{data.data.audit.items.map((event) => (
									<li key={event.id} className="py-2 first:pt-0 last:pb-0">
										<p className="text-[13.5px] text-ink-800">{event.action}</p>
										<p className="text-[12px] text-ink-500">
											{event.reason} · {formatDateTime(event.createdAt)}
										</p>
									</li>
								))}
							</ul>
						</Card>
					</div>
				</div>
			)}
		</>
	);
}

// ---------------------------------------------------------------------------
// Members / Invites / Roles
// ---------------------------------------------------------------------------

export function MembersPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(() => client.listMembers(scope), [client, scope.tenantId, reloadSeq]);
	const updateRole = useAction(async (memberId: string, role: Role) =>
		client.updateMemberRole(scope, memberId, role),
	);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="メンバー"
				description="権限を変更すると、利用できる画面と操作が変わります。"
				actions={<LinkButton to="/admin/invites">メンバーを招待</LinkButton>}
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["名前", "メールアドレス", "権限", "状態", "最終利用"]}>
						{data.data.map((member) => (
							<tr key={member.id}>
								<td className="font-semibold text-ink-900">{member.name}</td>
								<td className="text-ink-600">{member.email}</td>
								<td>
									<Select
										value={member.role}
										disabled={updateRole.pending || member.status !== "ACTIVE"}
										onChange={async (event) => {
											await updateRole.run(member.id, event.target.value as Role);
											setReloadSeq((n) => n + 1);
										}}
										aria-label={`${member.name} の権限`}
										className="w-36"
									>
										{ROLE_OPTIONS.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</Select>
								</td>
								<td>
									<StatusBadge status={member.status} />
								</td>
								<td className="text-ink-600">{formatDateTime(member.lastActiveAt)}</td>
							</tr>
						))}
					</Table>
					<ActionError error={updateRole.error} />
				</Card>
			)}
		</>
	);
}

export function InvitesPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [form, setForm] = useState<{ email: string; role: Role }>({
		email: "",
		role: "tenant_member",
	});
	const data = useAsync(() => client.listInvites(scope), [client, scope.tenantId, reloadSeq]);
	const create = useAction(async () => client.createInvite(scope, form));
	const revoke = useAction(async (id: string) => client.revokeInvite(scope, id));

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="招待"
				description="招待リンクは一度だけ表示されます。"
			/>
			<div className="grid gap-4 lg:grid-cols-[1fr_320px]">
				<Card title="招待中">
					{data.status === "loading" && <LoadingState />}
					{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
					{data.status === "ready" &&
						(data.data.length === 0 ? (
							<EmptyState title="招待中のメンバーはいません" />
						) : (
							<Table head={["メールアドレス", "権限", "状態", "期限", ""]}>
								{data.data.map((invite) => (
									<tr key={invite.id}>
										<td className="text-ink-900">{invite.email}</td>
										<td className="text-ink-600">
											{ROLE_OPTIONS.find((r) => r.value === invite.role)?.label ?? invite.role}
										</td>
										<td>
											<StatusBadge status={invite.status} />
										</td>
										<td className="text-ink-600">{formatDateTime(invite.expiresAt)}</td>
										<td>
											{invite.status === "PENDING" && (
												<Button
													size="sm"
													variant="danger"
													disabled={revoke.pending}
													onClick={async () => {
														await revoke.run(invite.id);
														setReloadSeq((n) => n + 1);
													}}
												>
													取り消す
												</Button>
											)}
										</td>
									</tr>
								))}
							</Table>
						))}
				</Card>

				<Card title="招待する">
					<form
						className="space-y-3"
						onSubmit={async (event) => {
							event.preventDefault();
							const created = await create.run();
							if (created) {
								setForm({ email: "", role: "tenant_member" });
								setReloadSeq((n) => n + 1);
							}
						}}
					>
						<Field label="メールアドレス">
							<TextInput
								type="email"
								value={form.email}
								onChange={(event) => setForm({ ...form, email: event.target.value })}
								required
							/>
						</Field>
						<Field label="権限">
							<Select
								value={form.role}
								onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
							>
								{ROLE_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</Select>
						</Field>
						<Button type="submit" className="w-full" disabled={create.pending}>
							招待を作成
						</Button>
						<ActionError error={create.error} />
					</form>

					{create.result && (
						<div className="mt-3">
							<Callout tone="success" title="招待リンクを作成しました">
								<p className="mt-1 break-all font-mono text-[11.5px]">
									/invite/{create.result.token}
								</p>
								<p className="mt-1 text-[12px]">この画面を離れると再表示できません。</p>
							</Callout>
						</div>
					)}
				</Card>
			</div>
		</>
	);
}

export function RolesPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(() => client.listRoleDefinitions(scope), [client, scope.tenantId]);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="ロールと権限"
				description="Platformが固定している権限は、組織側で変更できません。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="grid gap-4 lg:grid-cols-2">
					{data.data.map((role) => (
						<Card key={role.role} title={role.displayName} description={role.description}>
							<div className="flex flex-wrap gap-1.5">
								{role.permissions.map((permission) => (
									<Badge
										key={permission}
										tone={role.lockedPermissions.includes(permission) ? "warning" : "neutral"}
									>
										{permission}
										{role.lockedPermissions.includes(permission) && " 🔒"}
									</Badge>
								))}
							</div>
							{role.lockedPermissions.length > 0 && (
								<p className="mt-2 text-[12px] text-ink-500">
									🔒 の権限はPlatform Policyで固定されており、変更できません。
								</p>
							)}
						</Card>
					))}
				</div>
			)}
		</>
	);
}

export function TenantProjectsPage() {
	const { client } = useApi();
	const scope = useScope();
	const { setProject } = useAuth();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [repositoryByProject, setRepositoryByProject] = useState<Record<string, string>>({});
	const data = useAsync(async () => {
		const [projects, sessions, repositories] = await Promise.all([
			client.listOrganizationProjects(scope.tenantId, { includeArchived: true }),
			client.listSessions(scope, { limit: 200 }),
			client.listOrganizationRepositories(scope.tenantId),
		]);
		const connections = await Promise.all(
			projects.map(
				async (project) =>
					[project.id, await client.listProjectRepositories(scope.tenantId, project.id)] as const,
			),
		);
		return { projects, sessions, repositories, connections: Object.fromEntries(connections) };
	}, [client, scope.tenantId, scope.projectId, reloadSeq]);
	const create = useAction(async () =>
		client.createProject(scope.tenantId, {
			name: name.trim(),
			description: description.trim(),
			workspaceType: "generic",
		}),
	);
	const archive = useAction(async (projectId: string) =>
		client.archiveProject(scope.tenantId, projectId),
	);
	const restore = useAction(async (projectId: string) =>
		client.restoreProject(scope.tenantId, projectId),
	);
	const connect = useAction(async (projectId: string, repositoryId: string) =>
		client.connectProjectRepository(scope.tenantId, projectId, repositoryId),
	);
	const disconnect = useAction(async (projectId: string, repositoryId: string) =>
		client.disconnectProjectRepository(scope.tenantId, projectId, repositoryId),
	);
	const archiveAndSelect = async (projectId: string) => {
		await archive.run(projectId);
		if (projectId === scope.projectId) {
			const remaining = await client.listOrganizationProjects(scope.tenantId);
			const next = remaining.find((project) => project.isDefault) ?? remaining[0];
			if (next) setProject(next.id, next.name);
		}
		setReloadSeq((value) => value + 1);
	};

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="プロジェクト"
				description="プロジェクトの作成、切り替え、リポジトリ接続を管理します。"
			/>
			<Card title="新しいプロジェクト" className="mb-4">
				<form
					className="grid gap-3 md:grid-cols-[1fr_2fr_auto] md:items-end"
					onSubmit={async (event) => {
						event.preventDefault();
						const created = await create.run();
						if (!created) return;
						setProject(created.id, created.name);
						setName("");
						setDescription("");
						setReloadSeq((value) => value + 1);
					}}
				>
					<Field label="プロジェクト名">
						<TextInput
							value={name}
							required
							maxLength={120}
							onChange={(e) => setName(e.target.value)}
						/>
					</Field>
					<Field label="説明">
						<TextInput
							value={description}
							maxLength={2000}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</Field>
					<Button type="submit" disabled={create.pending || !name.trim()}>
						{create.pending ? "作成中…" : "作成"}
					</Button>
				</form>
				<ActionError error={create.error} />
			</Card>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["プロジェクト", "状態", "リポジトリ", "操作"]}>
						{data.data.projects.map((project) => {
							const connections = data.data.connections[project.id] ?? [];
							const connectedIds = new Set(connections.map((item) => item.repositoryId));
							const available = data.data.repositories.filter((repo) => !connectedIds.has(repo.id));
							return (
								<tr key={project.id}>
									<td>
										<p className="font-semibold text-ink-900">{project.name}</p>
										<p className="text-[12px] text-ink-500">{project.description}</p>
									</td>
									<td>
										<StatusBadge status={project.status ?? "ACTIVE"} />
									</td>
									<td className="min-w-64">
										<div className="flex flex-wrap gap-2">
											{connections.map((item) => (
												<Button
													key={item.repositoryId}
													size="sm"
													variant="ghost"
													onClick={async () => {
														await disconnect.run(project.id, item.repositoryId);
														setReloadSeq((value) => value + 1);
													}}
												>
													{item.repositoryName} を解除
												</Button>
											))}
										</div>
										{project.status !== "ARCHIVED" && available.length > 0 && (
											<div className="mt-2 flex gap-2">
												<Select
													value={repositoryByProject[project.id] ?? available[0]?.id ?? ""}
													onChange={(e) =>
														setRepositoryByProject((current) => ({
															...current,
															[project.id]: e.target.value,
														}))
													}
													aria-label={`${project.name} に接続するリポジトリ`}
												>
													{available.map((repo) => (
														<option key={repo.id} value={repo.id}>
															{repo.name}
														</option>
													))}
												</Select>
												<Button
													size="sm"
													onClick={async () => {
														await connect.run(
															project.id,
															repositoryByProject[project.id] ?? available[0]!.id,
														);
														setReloadSeq((value) => value + 1);
													}}
												>
													接続
												</Button>
											</div>
										)}
									</td>
									<td>
										{project.status === "ARCHIVED" ? (
											<Button
												size="sm"
												variant="ghost"
												onClick={async () => {
													await restore.run(project.id);
													setReloadSeq((value) => value + 1);
												}}
											>
												復元
											</Button>
										) : (
											<div className="flex gap-2">
												<Button
													size="sm"
													variant="ghost"
													onClick={() => setProject(project.id, project.name)}
												>
													切り替え
												</Button>
												<Button
													size="sm"
													variant="danger"
													onClick={() => archiveAndSelect(project.id)}
												>
													アーカイブ
												</Button>
											</div>
										)}
									</td>
								</tr>
							);
						})}
					</Table>
					<ActionError
						error={archive.error ?? restore.error ?? connect.error ?? disconnect.error}
					/>
				</Card>
			)}
		</>
	);
}

// ---------------------------------------------------------------------------
// Catalog / Auth pools / BYO / Provider rules
// ---------------------------------------------------------------------------

export function TenantCatalogPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(async () => {
		const [catalog, entitlements] = await Promise.all([
			client.getCatalog(scope),
			client.listCatalogEntitlements(scope),
		]);
		return { catalog, entitlements };
	}, [client, scope.tenantId, scope.projectId, reloadSeq]);
	const toggle = useAction(async (ucsId: string, enabled: boolean) =>
		client.setCatalogEntitlement(scope, ucsId, enabled),
	);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="利用可能なAgent / UCS"
				description="有効にしたUCSだけが、利用者のAgent一覧に表示されます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["UCS", "業種", "公開状態", "利用可否"]}>
						{data.data.entitlements.map((entitlement) => {
							const ucs = data.data.catalog.ucs.find((u) => u.id === entitlement.ucsId);
							return (
								<tr key={entitlement.ucsId}>
									<td>
										<p className="font-semibold text-ink-900">
											{ucs?.displayName ?? entitlement.ucsId}
										</p>
										<p className="font-mono text-[11.5px] text-ink-400">{entitlement.ucsId}</p>
									</td>
									<td className="text-ink-600">{ucs?.verticalKey ?? "汎用"}</td>
									<td>{ucs && <StatusBadge status={ucs.status} />}</td>
									<td>
										<Toggle
											checked={entitlement.enabled}
											disabled={toggle.pending || ucs?.status !== "PUBLISHED"}
											disabledReason={
												ucs?.status !== "PUBLISHED" ? "未公開のUCSは有効化できません。" : undefined
											}
											label={entitlement.enabled ? "利用できる" : "利用できない"}
											onChange={async (next) => {
												await toggle.run(entitlement.ucsId, next);
												setReloadSeq((n) => n + 1);
											}}
										/>
										{!entitlement.enabled && entitlement.restrictedReason && (
											<p className="text-[12px] text-ink-500">{entitlement.restrictedReason}</p>
										)}
									</td>
								</tr>
							);
						})}
					</Table>
					<ActionError error={toggle.error} />
				</Card>
			)}
		</>
	);
}

export function AuthPoolsPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(async () => {
		const [assignments, members] = await Promise.all([
			client.listPoolAssignments(scope),
			client.listMembers(scope),
		]);
		return { assignments, members };
	}, [client, scope.tenantId, reloadSeq]);
	const setAssignment = useAction(
		async (poolId: string, subjectId: string, effect: "ALLOW" | "DENY") =>
			client.setPoolAssignment(scope, { poolId, subjectId, effect }),
	);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="共有AI接続の割当"
				description="Platformが用意した共有の接続を、誰に使わせるかを決めます。"
			/>

			<Callout tone="neutral" title="利用者には接続の別名だけが見えます">
				利用者の画面には「Claude Coding Standard」のような別名だけを表示します。
				実際のアカウント、ログインID、秘密情報は表示しません。
			</Callout>

			<div className="mt-4">
				{data.status === "loading" && <LoadingState />}
				{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
				{data.status === "ready" && (
					<div className="space-y-4">
						<Card title="現在の割当">
							<Table head={["接続の別名", "対象", "可否", "用途", "期間"]}>
								{data.data.assignments.map((assignment) => (
									<tr key={assignment.id}>
										<td className="font-semibold text-ink-900">{assignment.poolAlias}</td>
										<td className="text-ink-600">{assignment.subjectLabel}</td>
										<td>
											<Badge tone={assignment.effect === "ALLOW" ? "success" : "danger"}>
												{assignment.effect === "ALLOW" ? "許可" : "禁止"}
											</Badge>
										</td>
										<td className="text-ink-600">
											{assignment.allowedPurposes.length === 0
												? "—"
												: assignment.allowedPurposes.join(" / ")}
										</td>
										<td className="text-ink-600">
											{formatDateTime(assignment.validFrom)} 〜{" "}
											{assignment.validUntil ? formatDateTime(assignment.validUntil) : "無期限"}
										</td>
									</tr>
								))}
							</Table>
						</Card>

						<Card title="メンバーごとの利用可否" description="Coding用途の共有接続">
							<Table head={["メンバー", "現在", "操作"]}>
								{data.data.members
									.filter((member) => member.status === "ACTIVE")
									.map((member) => {
										const current = data.data.assignments.find(
											(a) => a.poolId === "pool-claude-coding" && a.subjectLabel === member.name,
										);
										const allowed = current?.effect === "ALLOW";
										return (
											<tr key={member.id}>
												<td className="text-ink-900">{member.name}</td>
												<td>
													<Badge tone={allowed ? "success" : "neutral"}>
														{allowed ? "利用できる" : "利用できない"}
													</Badge>
												</td>
												<td>
													<Button
														size="sm"
														variant={allowed ? "danger" : "secondary"}
														disabled={setAssignment.pending}
														onClick={async () => {
															await setAssignment.run(
																"pool-claude-coding",
																memberUserId(member.email),
																allowed ? "DENY" : "ALLOW",
															);
															setReloadSeq((n) => n + 1);
														}}
													>
														{allowed ? "利用を止める" : "利用を許可"}
													</Button>
												</td>
											</tr>
										);
									})}
							</Table>
							<ActionError error={setAssignment.error} />
						</Card>
					</div>
				)}
			</div>
		</>
	);
}

/** Mock DatasetのUser IDへ対応させる簡易マッピング。 */
function memberUserId(email: string): string {
	switch (email) {
		case "user@example.test":
			return "user-01";
		case "admin@example.test":
			return "user-02";
		case "member2@example.test":
			return "user-04";
		default:
			return email;
	}
}

export function ByoPolicyPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(() => client.getByoPolicy(scope), [client, scope.tenantId, reloadSeq]);
	const update = useAction(async (input: { byokAllowed: boolean; byosAllowed: boolean }) =>
		client.setByoPolicy(scope, input),
	);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="個人AI接続の可否"
				description="メンバーが自分のAPI KeyやSubscriptionを使えるかを決めます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="grid gap-4 lg:grid-cols-2">
					<Card title="許可設定">
						<div className="space-y-3">
							<Toggle
								checked={data.data.byokAllowed}
								disabled={!data.data.platformByokAllowed || update.pending}
								disabledReason={
									!data.data.platformByokAllowed
										? "Platform Policyが個人API Keyを禁止しているため許可できません。"
										: undefined
								}
								label="個人API Key（BYOK）の利用を許可する"
								onChange={async (next) => {
									await update.run({ byokAllowed: next, byosAllowed: data.data.byosAllowed });
									setReloadSeq((n) => n + 1);
								}}
							/>
							<Toggle
								checked={data.data.byosAllowed}
								disabled={!data.data.platformByosAllowed || update.pending}
								disabledReason={
									!data.data.platformByosAllowed
										? "Platform Policyが個人Subscriptionを禁止しているため許可できません。"
										: undefined
								}
								label="個人Subscription（BYOS）の利用を許可する"
								onChange={async (next) => {
									await update.run({ byokAllowed: data.data.byokAllowed, byosAllowed: next });
									setReloadSeq((n) => n + 1);
								}}
							/>
						</div>
						<ActionError error={update.error} />
					</Card>

					<Card title="登録できるAI提供元">
						<div className="flex flex-wrap gap-1.5">
							{data.data.allowedProviderIds.map((providerId) => (
								<Badge key={providerId} tone="neutral">
									{providerId}
								</Badge>
							))}
						</div>
						{data.data.note && (
							<Callout tone="warning" title="制限の理由">
								{data.data.note}
							</Callout>
						)}
						<p className="mt-3 text-[12px] text-ink-500">
							上位のProvider Policyより広い範囲を、組織側で許可することはできません。
						</p>
					</Card>
				</div>
			)}
		</>
	);
}

export function ProviderRulesPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(() => client.listProviderUsageRules(scope), [client, scope.tenantId]);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="Provider利用ルール"
				description="用途ごとに、どのAIを優先するかの設定です。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<Card>
					<Table head={["用途", "AI提供元", "拘束力", "優先度", "変更"]}>
						{data.data.map((rule) => (
							<tr key={rule.id}>
								<td className="font-semibold text-ink-900">{rule.taskType}</td>
								<td className="text-ink-600">{rule.providerId}</td>
								<td>
									<Badge tone={rule.routeConstraintMode === "REQUIRED" ? "danger" : "neutral"}>
										{rule.routeConstraintMode === "REQUIRED"
											? "固定（切替不可）"
											: "優先（切替可）"}
									</Badge>
								</td>
								<td className="text-ink-600">{rule.priority}</td>
								<td>
									{rule.lockedByPlatform ? (
										<span className="text-[13px] text-ink-500">🔒 Platform固定</span>
									) : (
										<span className="text-[13px] text-ink-500">変更可（POCでは未接続）</span>
									)}
								</td>
							</tr>
						))}
					</Table>
					<p className="mt-2 text-[12px] text-ink-500">
						🔒 はSecurity / Compliance由来の固定です。組織側から「優先」へ緩めることはできません。
					</p>
				</Card>
			)}
		</>
	);
}

// ---------------------------------------------------------------------------
// Storage / Budget / Audit / Integrations / Settings
// ---------------------------------------------------------------------------

export function TenantStoragePage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(async () => {
		const [objects, artifacts] = await Promise.all([
			client.listStorageObjects(scope),
			client.listArtifacts(scope, { limit: 200 }),
		]);
		return { objects, artifacts };
	}, [client, scope.tenantId, scope.projectId]);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="テナント保管庫"
				description="組織内すべての成果物と保存状態です。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<>
					<div className="mb-4 grid gap-3 sm:grid-cols-3">
						<Metric label="保存オブジェクト" value={data.data.objects.length} unit="件" />
						<Metric
							label="合計サイズ"
							value={`${(data.data.objects.reduce((s, o) => s + o.size, 0) / 1024).toFixed(1)}`}
							unit="KB"
						/>
						<Metric
							label="長期保管"
							value={data.data.objects.filter((o) => o.storageClass === "ARCHIVE").length}
							unit="件"
						/>
					</div>
					<Card>
						<Table head={["成果物", "所有者", "サイズ", "チェックサム", "保管区分", "保管期限"]}>
							{data.data.objects.map((object) => {
								const artifact = data.data.artifacts.items.find((a) => a.id === object.artifactId);
								return (
									<tr key={object.id}>
										<td className="text-ink-900">{artifact?.name ?? object.artifactId}</td>
										<td className="text-ink-600">{artifact?.ownerUserId ?? "—"}</td>
										<td className="text-ink-600">{object.size.toLocaleString()} B</td>
										<td className="font-mono text-[11.5px] text-ink-500">
											{object.checksum.slice(0, 20)}…
										</td>
										<td>
											<Badge tone={object.storageClass === "ARCHIVE" ? "warning" : "neutral"}>
												{object.storageClass}
											</Badge>
										</td>
										<td className="text-ink-600">
											{object.retentionUntil ? formatDateTime(object.retentionUntil) : "期限なし"}
										</td>
									</tr>
								);
							})}
						</Table>
					</Card>
				</>
			)}
		</>
	);
}

export function BudgetPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [drafts, setDrafts] = useState<Record<string, string>>({});
	const data = useAsync(async () => {
		const [budgets, usage] = await Promise.all([
			client.listBudgets(scope),
			client.getUsageSummary(scope),
		]);
		return { budgets, usage };
	}, [client, scope.tenantId, scope.projectId, reloadSeq]);
	const update = useAction(async (id: string, amount: number) =>
		client.updateBudget(scope, id, amount),
	);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="予算・上限・利用量"
				description="超過時の挙動は、実行前のGateとして働きます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="space-y-4">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<Metric label="実行数" value={data.data.usage.executions} unit="件" />
						<Metric label="成果物" value={data.data.usage.artifacts} unit="件" />
						<Metric label="入力トークン" value={data.data.usage.inputTokens.toLocaleString()} />
						<Metric
							label="費用"
							value={`¥${Math.round(data.data.usage.amount).toLocaleString()}`}
						/>
					</div>

					<Card title="予算">
						<Table head={["対象", "上限", "消化", "予約", "超過時", ""]}>
							{data.data.budgets.map((budget) => (
								<tr key={budget.id}>
									<td className="font-semibold text-ink-900">{budget.scopeLabel}</td>
									<td>
										<TextInput
											type="number"
											min={0}
											className="w-32"
											value={drafts[budget.id] ?? String(budget.limitAmount)}
											onChange={(event) =>
												setDrafts({ ...drafts, [budget.id]: event.target.value })
											}
											aria-label={`${budget.scopeLabel} の上限`}
										/>
									</td>
									<td className="text-ink-600">¥{budget.consumedAmount.toLocaleString()}</td>
									<td className="text-ink-600">¥{budget.reservedAmount.toLocaleString()}</td>
									<td>
										<Badge
											tone={
												budget.onExceeded === "BLOCK"
													? "danger"
													: budget.onExceeded === "REQUIRE_APPROVAL"
														? "warning"
														: "neutral"
											}
										>
											{budget.onExceeded === "BLOCK"
												? "実行停止"
												: budget.onExceeded === "REQUIRE_APPROVAL"
													? "承認必須"
													: "警告のみ"}
										</Badge>
									</td>
									<td>
										<Button
											size="sm"
											variant="secondary"
											disabled={update.pending}
											onClick={async () => {
												const raw = drafts[budget.id] ?? String(budget.limitAmount);
												await update.run(budget.id, Number(raw));
												setReloadSeq((n) => n + 1);
											}}
										>
											保存
										</Button>
									</td>
								</tr>
							))}
						</Table>
						<ActionError error={update.error} />
					</Card>
				</div>
			)}
		</>
	);
}

export function AuditPage() {
	const { client } = useApi();
	const scope = useScope();
	const [query, setQuery] = useState("");
	const data = useAsync(
		() => client.listAuditEvents(scope, { limit: 200 }),
		[client, scope.tenantId, scope.projectId],
	);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="監査ログ"
				description="設定・権限・認証・承認・所有者移管の操作が記録されます。秘密情報は含みません。"
			/>
			<div className="mb-4 max-w-sm">
				<TextInput
					type="search"
					placeholder="操作名や理由で絞り込む"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					aria-label="監査ログを絞り込む"
				/>
			</div>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(() => {
					const keyword = query.trim().toLowerCase();
					const visible = keyword
						? data.data.items.filter((event) =>
								`${event.action}${event.reason}${event.resourceType}${event.resourceId}`
									.toLowerCase()
									.includes(keyword),
							)
						: data.data.items;
					if (visible.length === 0) {
						return (
							<EmptyState
								title="該当する記録がありません"
								description="条件を変えてお試しください。"
							/>
						);
					}
					return (
						<Card>
							<Table head={["日時", "操作", "対象", "実行者", "理由", "Trace"]}>
								{visible.map((event) => (
									<tr key={event.id}>
										<td className="text-ink-600">{formatDateTime(event.createdAt)}</td>
										<td className="font-semibold text-ink-900">{event.action}</td>
										<td className="text-ink-600">
											{event.resourceType}
											<span className="block font-mono text-[11px] text-ink-400">
												{event.resourceId}
											</span>
										</td>
										<td className="text-ink-600">{event.actorId}</td>
										<td className="text-ink-600">{event.reason}</td>
										<td className="font-mono text-[11px] text-ink-400">{event.traceId}</td>
									</tr>
								))}
							</Table>
						</Card>
					);
				})()}
		</>
	);
}

export function IntegrationsPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(() => client.listIntegrations(scope), [client, scope.tenantId]);

	return (
		<>
			<PageHeader
				eyebrow="組織管理"
				title="外部連携"
				description="接続情報は参照のみを保持します。秘密情報の本文は保存しません。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="grid gap-4 lg:grid-cols-2">
					{data.data.map((integration) => (
						<Card
							key={integration.id}
							title={integration.displayName}
							description={integration.kind}
							action={<StatusBadge status={integration.status} />}
						>
							<DefinitionList
								columns={1}
								items={[
									{ label: "参照", value: integration.secretRef ?? "未設定", mono: true },
									{ label: "最終同期", value: formatDateTime(integration.lastSyncedAt) },
								]}
							/>
							{integration.status === "ERROR" && (
								<Callout tone="danger" title="連携が失敗しています">
									接続先の設定を確認し、参照を登録し直してください。
								</Callout>
							)}
						</Card>
					))}
				</div>
			)}
		</>
	);
}

export function TenantSettingsPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(() => client.getTenantSettings(scope), [client, scope.tenantId, reloadSeq]);
	const update = useAction(async (input: Record<string, unknown>) =>
		client.updateTenantSettings(scope, input),
	);

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;

	const settings = data.data;

	return (
		<>
			<PageHeader eyebrow="組織管理" title="組織設定" />
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="基本情報">
					<DefinitionList
						columns={1}
						items={[
							{ label: "組織名", value: settings.displayName },
							{ label: "識別子", value: settings.slug, mono: true },
							{ label: "言語", value: settings.locale === "ja" ? "日本語" : "English" },
							{ label: "データ保管地域", value: settings.dataResidency },
						]}
					/>
				</Card>

				<Card title="運用ルール">
					<div className="space-y-3">
						<Toggle
							checked={settings.requireApprovalForPublish}
							disabled={update.pending}
							label="公開前に必ず承認を求める"
							onChange={async (next) => {
								await update.run({ requireApprovalForPublish: next });
								setReloadSeq((n) => n + 1);
							}}
						/>
						<Field label="会話・成果物の保存期間（日）">
							<TextInput
								type="number"
								min={30}
								defaultValue={settings.sessionRetentionDays}
								onBlur={async (event) => {
									const value = Number(event.target.value);
									if (value !== settings.sessionRetentionDays) {
										await update.run({ sessionRetentionDays: value });
										setReloadSeq((n) => n + 1);
									}
								}}
							/>
						</Field>
					</div>
					<ActionError error={update.error} />
				</Card>
			</div>
		</>
	);
}
